import React, { useState, useEffect, useRef } from "react";
import { socket } from "../socket";
import { gameUrl } from "../router";
import { t } from "../i18n";
import { PlayerAvatar, PlayerName, HostCrown, PinnedMedals, bannerStyle } from "./PlayerIdentity";
import { useSounds } from "../sound";
import Dropdown from "./Dropdown";
import Heart from "./Heart";
import QRCode from "qrcode";
import { confirmDialog, alertDialog } from "../dialog";

// Small "copy" glyph (functional control, not a decorative icon)
// Un QR code stylisé : trois repères d'angle et quelques modules.
function QrGlyph() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false" fill="currentColor">
      <path d="M3 3h7v7H3zm2 2v3h3V5zM14 3h7v7h-7zm2 2v3h3V5zM3 14h7v7H3zm2 2v3h3v-3zM14 14h3v3h-3zM18 18h3v3h-3zM18 14h3v2h-3zM14 18h2v3h-2z" fillRule="evenodd" />
    </svg>
  );
}

function CopyGlyph() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false">
      <rect x="8" y="8" width="12" height="12" rx="2.5" fill="none" stroke="currentColor" stroke-width="1.8" />
      <path d="M16 8V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h2" fill="none" stroke="currentColor" stroke-width="1.8" />
    </svg>
  );
}

// Values offered in the lobby settings (kept in sync with the server bounds)
const NUM_QUESTIONS_CHOICES = [20, 25, 30, 35, 40];
const QUESTION_TIME_CHOICES = [15, 20, 25, 30, 35, 40, 45];
// Aligné sur QUESTION_TIME_DEFAULT côté serveur : le lobby affiche la bonne
// valeur avant même que le serveur ne la confirme.
const QUESTION_TIME_DEFAULT = 30;
const CACO_SONGS_CHOICES    = [2, 3, 4, 5];
const MAX_PLAYERS_CHOICES   = [2, 4, 6, 8, 10, 12, 0];  // 0 : illimité
const BR_LIVES_CHOICES      = [1, 2, 3, 4, 5];
const BO_LIVES_CHOICES      = [1, 2, 3, 4, 5];

// Cards of the "Mode" tab: order shown, and which ones are playable.
// Labels and descriptions come from the dictionaries (modes.* / modeCards.*).
const GAME_MODES = [
  { slug: "culture" },
  { slug: "blindtest" },
  { slug: "buzzer" },
  { slug: "pictionary" },
  { slug: "bombe" },
  { slug: "battleroyale" },
  { slug: "references" },
  { slug: "pixelguess" },
  { slug: "smashpass" },
  { slug: "hemicycle" },
  { slug: "undercover" },
];

function GameLobby({player, players, gameId, onStop, onLeave}) {
  const [numQuestions, setNumQuestions] = useState(NUM_QUESTIONS_CHOICES[2]);
  const [questionTime, setQuestionTime] = useState(QUESTION_TIME_DEFAULT);
  const [maxPlayers, setMaxPlayers] = useState(8);
  const [themes, setThemes] = useState([]);
  const [selectedThemes, setSelectedThemes] = useState([]);
  const [selectedMode, setSelectedMode] = useState("culture");
  const [impVariant, setImpVariant] = useState("manches");
  const [btVariant, setBtVariant] = useState("classique");
  const [pl, setPl] = useState({ plScoring: "mot", plTurn: "simultane", plLevel: "melange" });
  const [btCacoSongs, setBtCacoSongs] = useState(3);
  const [brLives, setBrLives] = useState(3);
  const [boLives, setBoLives] = useState(2);
  const [boDifficulty, setBoDifficulty] = useState("normal");
  const [boAvailable, setBoAvailable] = useState(true);
  const [pgScoring, setPgScoring] = useState("points");
  const [rfMode, setRfMode] = useState("detente");
  const [rfSize, setRfSize] = useState("moyenne");
  const [rfPlacement, setRfPlacement] = useState("normal");
  const [piRounds, setPiRounds] = useState(3);
  const [btStart, setBtStart] = useState("debut");
  const [btShowTheme, setBtShowTheme] = useState(false);
  const [btTolerance, setBtTolerance] = useState("souple");
  const [btScoring, setBtScoring] = useState("fixe");
  // Modes ouverts au jeu : l'administration peut en fermer le temps d'y travailler
  const [openModes, setOpenModes] = useState(null);
  const [bzJokers, setBzJokers] = useState([]);
  const [bzJokerMode, setBzJokerMode] = useState("impose");
  const [bzRoundMode, setBzRoundMode] = useState("choisies");
  const [bzCatalogue, setBzCatalogue] = useState([]);
  const [bzRounds, setBzRounds] = useState([]);
  const [bzRoundCat, setBzRoundCat] = useState([]);
  const [bzExclusive, setBzExclusive] = useState([]);
  const [bzMax, setBzMax] = useState(4);
  const [activePanel, setActivePanel] = useState("gameModes");
  const [uc, setUc] = useState({ endMode: "eliminations", count: "auto", white: false, rounds: 3 });
  const [qrImage, setQrImage] = useState(null);   // QR code de l'invitation, affiché à la demande
  const [toastMsg, setToastMsg] = useState("");
  const toastRef = useRef(null);
  const toastTimer = useRef(null);

  const { playPop } = useSounds();

  // Arrivée d'un joueur : un petit signal suffit, personne ne fixe la liste
  const playerCount = players.length;
  const knownCount = useRef(playerCount);
  useEffect(() => {
    if (playerCount > knownCount.current) playPop();
    knownCount.current = playerCount;
  }, [playerCount, playPop]);

  const isHost = players.find(p => p.id === socket.id)?.isHost || false;
  // Puce de joueur dépliée (son id de socket), refermée au moindre clic ailleurs.
  const [openChip, setOpenChip] = useState(null);
  useEffect(() => {
    if (!openChip) return;
    const close = () => setOpenChip(null);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [openChip]);

  useEffect(() => {
    socket.on("themesUpdated", ({ themes }) => setSelectedThemes(themes));
    socket.on("availableThemes", ({ themes }) => {
      setThemes(themes);
      setSelectedThemes(themes);
    });
    socket.on("numQuestionsUpdated", ({ numQuestions }) => setNumQuestions(numQuestions));
    socket.on("questionTimeUpdated", ({ questionTime }) => setQuestionTime(questionTime));
    socket.on("maxPlayersUpdated", ({ maxPlayers }) => setMaxPlayers(maxPlayers));
    socket.on("gameModeUpdated", ({ gameMode }) => setSelectedMode(gameMode));
    socket.on("availableModes", ({ modes }) => setOpenModes(modes || []));
    socket.on("modeError", ({ error }) => alertDialog(error));
    socket.on("imposterVariantUpdated", ({ variant }) => setImpVariant(variant));
    socket.on("btVariantUpdated", ({ btVariant, btCacoSongs, btStart, btShowTheme, btTolerance, btScoring, plScoring, plTurn, plLevel }) => {
      if (plScoring) setPl({ plScoring, plTurn, plLevel });
      if (btScoring) setBtScoring(btScoring);
      if (btVariant) setBtVariant(btVariant);
      if (btStart) setBtStart(btStart);
      if (btCacoSongs) setBtCacoSongs(btCacoSongs);
      if (btShowTheme !== undefined) setBtShowTheme(!!btShowTheme);
      if (btTolerance) setBtTolerance(btTolerance);
    });
    socket.on("brLivesUpdated", ({ lives }) => setBrLives(lives));
    socket.on("boOptionsUpdated", ({ lives, difficulty, available }) => {
      setBoLives(lives); setBoDifficulty(difficulty); setBoAvailable(available);
    });
    socket.on("pgScoringUpdated", ({ scoring }) => setPgScoring(scoring));
    socket.on("rfModeUpdated", ({ mode, size, placement }) => {
      setRfMode(mode); if (size) setRfSize(size); if (placement) setRfPlacement(placement);
    });
    socket.on("piRoundsUpdated", ({ rounds }) => setPiRounds(rounds));
    socket.on("ucSettingsUpdated", setUc);
    socket.on("bzRoundsUpdated", ({ rounds, catalogue, mode }) => {
      if (rounds) setBzRounds(rounds);
      if (mode) setBzRoundMode(mode);
      if (catalogue) setBzRoundCat(catalogue);
    });
    socket.on("bzJokersUpdated", ({ jokers, catalogue, exclusive, count, mode }) => {
      if (jokers) setBzJokers(jokers);
      if (mode) setBzJokerMode(mode);
      if (catalogue) setBzCatalogue(catalogue);
      if (exclusive) setBzExclusive(exclusive);
      if (count) setBzMax(count);
    });
    socket.on("impError", ({ error }) => alertDialog(error));
    socket.on("brError", ({ error }) => alertDialog(error));
    socket.on("bzError", ({ error }) => alertDialog(error));
    socket.on("boError", ({ error }) => alertDialog(error));
    socket.on("pgError", ({ error }) => alertDialog(error));
    socket.on("rfError", ({ error }) => alertDialog(error));
    socket.on("piError", ({ error }) => alertDialog(error));
    // Le Blind Test et le Smash or Pass refusent aussi de démarrer (pas de
    // musique, pas d'image) : sans écoute ici, le bouton ne faisait rien.
    socket.on("btError", ({ error }) => alertDialog(error));
    socket.on("spError", ({ error }) => alertDialog(error));

    // Ask for the current lobby state: events sent by the server on creation /
    // connection can arrive before this component is mounted.
    socket.emit("getGameState", { gameId });

    return () => {
      socket.off("themesUpdated");
      socket.off("availableThemes");
      socket.off("numQuestionsUpdated");
      socket.off("questionTimeUpdated");
      socket.off("maxPlayersUpdated");
      socket.off("gameModeUpdated");
      socket.off("availableModes");
      socket.off("modeError");
      socket.off("imposterVariantUpdated");
      socket.off("btVariantUpdated");
      socket.off("brLivesUpdated");
      socket.off("boOptionsUpdated");
      socket.off("pgScoringUpdated");
      socket.off("rfModeUpdated");
      socket.off("piRoundsUpdated");
      socket.off("ucSettingsUpdated");
      socket.off("bzJokersUpdated");
      socket.off("bzRoundsUpdated");
      socket.off("impError");
      socket.off("brError");
      socket.off("bzError");
      socket.off("boError");
      socket.off("pgError");
      socket.off("rfError");
      socket.off("piError");
      socket.off("btError");
      socket.off("spError");
    };
  }, [gameId]);

  useEffect(() => () => clearTimeout(toastTimer.current), []);

  // Host changes the themes
  const singleTheme = selectedMode === "smashpass" || (selectedMode === "buzzer" && bzRoundMode === "standard");
  const setAllThemes = (all) => {
    if (!isHost) return;
    const next = all ? [...themes] : [];
    setSelectedThemes(next);
    socket.emit("updateThemes", { gameId, themes: next });
  };

  const showQr = async () => {
    try { setQrImage(await QRCode.toDataURL(gameUrl(gameId), { width: 320, margin: 2 })); }
    catch (_) { copyText(gameUrl(gameId), t("lobby.linkCopied")); }
  };

  const handleThemeChange = (theme) => {
    if (!isHost) return;
    // Smash or Pass et le Buzzer standard se jouent sur un seul thème : le
    // choix en remplace un autre
    if (singleTheme) {
      setSelectedThemes([theme]);
      socket.emit("updateThemes", { gameId, themes: [theme] });
      return;
    }
    let newThemes;
    if (selectedThemes.includes(theme)) {
      newThemes = selectedThemes.filter(t => t !== theme);
    } else {
      newThemes = [...selectedThemes, theme];
    }
    setSelectedThemes(newThemes);
    socket.emit("updateThemes", { gameId, themes: newThemes });
  };

  const handleNumQuestionsChange = (value) => {
    if (!isHost) return;
    const clamped = Math.min(40, Math.max(20, value));
    setNumQuestions(clamped);
    socket.emit("updateNumQuestions", { gameId, numQuestions: clamped });
  };

  const handleQuestionTimeChange = (value) => {
    if (!isHost) return;
    const clamped = Math.min(
      QUESTION_TIME_CHOICES[QUESTION_TIME_CHOICES.length - 1],
      Math.max(QUESTION_TIME_CHOICES[0], value),
    );
    setQuestionTime(clamped);
    socket.emit("updateQuestionTime", { gameId, questionTime: clamped });
  };

  // The server will refuse to go below the number of players already present
  const handleMaxPlayersChange = (value) => {
    if (!isHost || (value > 0 && value < players.length)) return;
    setMaxPlayers(value);
    socket.emit("updateMaxPlayers", { gameId, maxPlayers: value });
  };

  const handleStart = () => {
    if (!isHost) return;
    socket.emit("startGame", {gameId});
  };

  const handleStop = async () => {
    if (!isHost) return;
    if (await confirmDialog(t("lobby.confirmClose"))) {
      onStop();
    }
  };

  const handleLeave = async () => {
    if (await confirmDialog(t("lobby.confirmLeave"))) {
      onLeave();
    }
  };

  const showToast = (msg) => {
    setToastMsg(msg);
    const el = toastRef.current;
    if (!el) return;
    el.classList.add('show');
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => el.classList.remove('show'), 2000);
  };

  // Shared fallback when the clipboard is unavailable (insecure context,
  // permission denied): show the text so it can be copied by hand.
  const copyText = async (text, msg) => {
    try {
      await navigator.clipboard.writeText(text);
      showToast(msg);
    } catch (_) {
      window.prompt(t("lobby.copyThis"), text);
    }
  };

  // Le bouton du code copie le code seul ; le lien complet passe par le
  // bouton d'invitation.
  const copyCode = () => copyText(gameId, t("lobby.codeCopied"));

  // Inviting = sharing the lobby address. On mobile, the device share sheet;
  // elsewhere (or if unavailable), copy the link.
  const shareInvite = async () => {
    const url = gameUrl(gameId);
    if (navigator.share) {
      try {
        await navigator.share({
          title: "PopuQuiz",
          text: t("lobby.shareText", { code: gameId }),
          url,
        });
        return;
      } catch (err) {
        if (err?.name === "AbortError") return; // share cancelled by the player
      }
    }
    copyText(url, t("lobby.linkCopied"));
  };

  const selectMode = (mode) => {
    if (!isHost || mode === selectedMode) return;
    setSelectedMode(mode);
    socket.emit("updateGameMode", { gameId, gameMode: mode });
  };

  // La Bombe tire ses syllabes du dictionnaire : elle n'a pas de banque de
  // questions, donc pas de thèmes à choisir.
  const hasThemes = selectedMode !== "bombe";
  useEffect(() => {
    if (!hasThemes && activePanel === "gameThemes") setActivePanel("gameOptions");
  }, [hasThemes, activePanel]);

  const tabs = [
    { key: "gameModes", label: t("lobby.tabMode") },
    ...(hasThemes ? [{ key: "gameThemes", label: t("lobby.tabThemes") }] : []),
    { key: "gameOptions", label: t("lobby.tabSettings") },
  ];

  return (
    <div class="GameLobby">
      <div class="toast success" ref={toastRef}>{toastMsg}</div>

      <div class="gl-top">
        <div class="gl-players">
          <span class="gl-players-count">{t("lobby.players", { count: players.length, max: maxPlayers || "∞" })}</span>
          {players.map(p => (
            <div key={p.id} class="gl-chip-wrap">
              {/* Un clic sur la puce ouvre, juste dessous, le pseudo et les
                  médailles épinglées du joueur. */}
              <div id={p.id} role="button" tabIndex={0} aria-expanded={openChip === p.id}
                class={`gl-chip${p.isHost ? " host" : ""}${p.id === socket.id ? " self" : ""}${p.cosmetics?.banner ? " has-banner" : ""}`}
                style={bannerStyle(p)}
                onClick={e => { e.stopPropagation(); setOpenChip(o => (o === p.id ? null : p.id)); }}
                onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpenChip(o => (o === p.id ? null : p.id)); } }}>
                <PlayerAvatar player={p} size={26} /><PlayerName player={p} />
                {p.isHost && <HostCrown size={15} />}
              </div>
              {openChip === p.id && (
                <div class="gl-chip-pop" onClick={e => e.stopPropagation()}>
                  <span class="gl-chip-pop-name">{p.pseudo}</span>
                  <div class="gl-chip-pop-medals"><PinnedMedals player={p} /></div>
                </div>
              )}
            </div>
          ))}
          <button class="gl-invite" onClick={shareInvite}>{t("lobby.invite")}</button>
        </div>
        {/* Le code d'un trait, la copie au bout : c'est ce qu'on dicte à voix
            haute, et le bouton rond dit qu'on peut l'emporter. */}
        <div class="gl-code-row">
        <button class="gl-qr" type="button" onClick={showQr} title={t("lobby.showQr")} aria-label={t("lobby.showQr")}>
          <QrGlyph />
        </button>
        <button class="gl-code" onClick={copyCode} title={t("lobby.copyCode")}>
          <b class="gl-code-value">{gameId}</b>
          <span class="gl-code-copy"><CopyGlyph /></span>
        </button>
        </div>
      </div>

      {qrImage && (
        <div class="gl-qr-modal" onClick={() => setQrImage(null)}>
          <div class="gl-qr-panel" onClick={e => e.stopPropagation()}>
            <img src={qrImage} alt={t("lobby.showQr")} width="320" height="320" />
            <b class="gl-qr-code">{gameId}</b>
            <span class="gl-qr-url">{gameUrl(gameId)}</span>
            <button type="button" class="btn btn-primary" onClick={() => setQrImage(null)}>{t("common.close")}</button>
          </div>
        </div>
      )}

      <div class="gl-tabs">
        {tabs.map(tab => (
          <button key={tab.key} class={`gl-tab${activePanel === tab.key ? " active" : ""}`}
            onClick={() => setActivePanel(tab.key)}>
            {tab.label}
          </button>
        ))}
      </div>

      <div class={`content-editor${isHost ? "" : " read-only"}`}>
        <div class={`gameModes${activePanel === "gameModes" ? " panel-active" : ""}`}>
          {/* Un mode éteint par l'administration n'apparaît pas du tout. */}
          {GAME_MODES.filter(m => openModes === null || openModes.includes(m.slug)).map(m => {
            const closed = m.disabled;
            // L'Imposteur se choisit dans les réglages de Culture : la carte
            // Culture reste éclairée tant qu'on est dans cette famille.
            const active = m.slug === selectedMode
              || (m.slug === "culture" && selectedMode === "imposteur");
            return (
            <div key={m.slug} gamemode={m.slug}
              title={closed ? t("lobby.modeClosed") : undefined}
              class={`gameModeCard${closed ? " disabled" : active ? " active" : ""}`}
              onClick={closed ? undefined : () => selectMode(m.slug)}>
              <div class="gameModeTitle">{t(`modes.${m.slug}`)}</div>
              <div class="gameModeDescription">{t(`modeCards.${m.slug}Desc`)}</div>
            </div>
            );
          })}
        </div>

        <div class={`gameThemes${activePanel === "gameThemes" ? " panel-active" : ""}`}>
          {isHost && !singleTheme && themes.length > 1 && (
            <div class="gl-themes-actions">
              <button type="button" class="btn btn-sm" onClick={() => setAllThemes(true)}>{t("lobby.selectAll")}</button>
              <button type="button" class="btn btn-sm" onClick={() => setAllThemes(false)}>{t("lobby.selectNone")}</button>
            </div>
          )}
          <div class="themes-list">
            {themes.map(theme => (
              <label id={theme}>
                <input
                  type={singleTheme ? "radio" : "checkbox"}
                  name={singleTheme ? "single-theme" : undefined}
                  checked={selectedThemes.includes(theme)}
                  disabled={!isHost}
                  onChange={() => handleThemeChange(theme)}
                />
                {theme}
              </label>
            ))}
          </div>
        </div>

        <div class={`gameOptions${activePanel === "gameOptions" ? " panel-active" : ""}`}>
          {selectedMode === "blindtest" && (
            <>
              <div class="gl-setting">
                <p>{t("lobby.variant")}</p>
                <Dropdown
                  value={btVariant}
                  disabled={!isHost}
                  ariaLabel={t("lobby.variant")}
                  options={[
                    { value: "classique", label: t("lobby.btClassic") },
                    { value: "cacophonie", label: t("lobby.btCacophony") },
                    { value: "interruption", label: t("lobby.btInterruption") },
                    { value: "paroles", label: t("lobby.btLyrics") },
                  ]}
                  onChange={v => { setBtVariant(v); socket.emit("updateBtVariant", { gameId, btVariant: v }); }}
                />
              </div>
              {/* Paroles : ses trois options ; le départ, la tolérance et le
                  barème du Blind Test ne s'y appliquent pas. */}
              {btVariant === "paroles" && [
                ["plLevel", "lobby.plLevel", [["facile", "lobby.plLevelEasy"], ["difficile", "lobby.plLevelHard"], ["melange", "lobby.plLevelMixed"]]],
                ["plScoring", "lobby.plScoring", [["mot", "lobby.plScoringWords"], ["toutourien", "lobby.plScoringAll"]]],
                ["plTurn", "lobby.plTurn", [["simultane", "lobby.plTurnAll"], ["chacun", "lobby.plTurnEach"]]],
              ].map(([key, label, opts]) => (
                <div class="gl-setting" key={key}>
                  <p>{t(label)}</p>
                  <Dropdown value={pl[key]} disabled={!isHost} ariaLabel={t(label)}
                    options={opts.map(([value, l]) => ({ value, label: t(l) }))}
                    onChange={v => { setPl(o => ({ ...o, [key]: v })); socket.emit("updateBtVariant", { gameId, [key]: v }); }} />
                </div>
              ))}
              {btVariant !== "cacophonie" && btVariant !== "paroles" && (
                <div class="gl-setting">
                  <p>{t("lobby.btStart")}</p>
                  <Dropdown
                    value={btStart}
                    disabled={!isHost}
                    ariaLabel={t("lobby.btStart")}
                    options={[
                      { value: "debut", label: t("lobby.btStartBeginning") },
                      { value: "milieu", label: t("lobby.btStartMiddle") },
                    ]}
                    onChange={v => { setBtStart(v); socket.emit("updateBtVariant", { gameId, btStart: v }); }}
                  />
                </div>
              )}
              {btVariant !== "paroles" && <>
              <div class="gl-setting">
                <p>{t("lobby.btTolerance")}</p>
                <Dropdown
                  value={btTolerance}
                  disabled={!isHost}
                  ariaLabel={t("lobby.btTolerance")}
                  options={[
                    { value: "souple", label: t("lobby.btToleranceLoose") },
                    { value: "stricte", label: t("lobby.btToleranceStrict") },
                  ]}
                  onChange={v => { setBtTolerance(v); socket.emit("updateBtVariant", { gameId, btTolerance: v }); }}
                />
              </div>
              <div class="gl-setting">
                <p>{t("lobby.btScoring")}</p>
                <Dropdown
                  value={btScoring}
                  disabled={!isHost}
                  ariaLabel={t("lobby.btScoring")}
                  options={[
                    { value: "fixe", label: t("lobby.btScoringFixed") },
                    { value: "rapidite", label: t("lobby.btScoringSpeed") },
                  ]}
                  onChange={v => { setBtScoring(v); socket.emit("updateBtVariant", { gameId, btScoring: v }); }}
                />
              </div>
              </>}
              <div class="gl-setting">
                <p>{t("lobby.btShowTheme")}</p>
                <Dropdown
                  value={btShowTheme ? "oui" : "non"}
                  disabled={!isHost}
                  ariaLabel={t("lobby.btShowTheme")}
                  options={[
                    { value: "non", label: t("lobby.btShowThemeOff") },
                    { value: "oui", label: t("lobby.btShowThemeOn") },
                  ]}
                  onChange={v => { setBtShowTheme(v === "oui"); socket.emit("updateBtVariant", { gameId, btShowTheme: v === "oui" }); }}
                />
              </div>
              {btVariant === "cacophonie" && (
                <div class="gl-setting">
                  <p>{t("lobby.simultaneousSongs")}</p>
                  <div class="gl-segment">
                    {CACO_SONGS_CHOICES.map(n => (
                      <button key={n} type="button" disabled={!isHost}
                        class={`gl-seg-btn${btCacoSongs === n ? " active" : ""}`}
                        onClick={() => { setBtCacoSongs(n); socket.emit("updateBtVariant", { gameId, btCacoSongs: n }); }}>
                        {n}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
          {(selectedMode === "culture" || selectedMode === "imposteur") && (
            <div class="gl-setting">
              <p>{t("lobby.cultureFormat")}</p>
              <Dropdown
                value={selectedMode === "imposteur" ? "imposteur" : "culture"}
                disabled={!isHost}
                ariaLabel={t("lobby.cultureFormat")}
                options={[
                  { value: "culture", label: t("lobby.cultureStandard") },
                  { value: "imposteur", label: t("modes.imposteur") },
                ]}
                onChange={selectMode}
              />
            </div>
          )}
          {selectedMode === "imposteur" && (
            <div class="gl-setting">
              <p>{t("lobby.imposterType")}</p>
              <Dropdown
                value={impVariant}
                disabled={!isHost}
                ariaLabel={t("lobby.imposterType")}
                options={[
                  { value: "manches", label: t("lobby.impRounds") },
                  { value: "incognito", label: t("lobby.impIncognito") },
                  { value: "volontaire", label: t("lobby.impVolunteer") },
                  { value: "taupe", label: t("lobby.impMole") },
                ]}
                onChange={v => { setImpVariant(v); socket.emit("updateImposterVariant", { gameId, variant: v }); }}
              />
            </div>
          )}
          <div class="gl-setting">
            <p>{t("lobby.maxPlayers")}</p>
            <div class="gl-segment">
              {MAX_PLAYERS_CHOICES.map(n => (
                <button key={n} type="button"
                  disabled={!isHost || (n > 0 && n < players.length)}
                  title={n === 0 ? t("lobby.unlimited") : n < players.length ? t("lobby.maxPlayersTaken", { count: players.length }) : undefined}
                  class={`gl-seg-btn${maxPlayers === n ? " active" : ""}`}
                  onClick={() => handleMaxPlayersChange(n)}>
                  {n === 0 ? "∞" : n}
                </button>
              ))}
            </div>
          </div>
          {selectedMode === "buzzer" && (
            <div class="gl-setting">
              <p>{t("buzzer.roundModeOption")}</p>
              <Dropdown
                value={bzRoundMode}
                disabled={!isHost}
                ariaLabel={t("buzzer.roundModeOption")}
                options={[
                  { value: "choisies", label: t("buzzer.roundModeChosen") },
                  { value: "aleatoires", label: t("buzzer.roundModeRandom") },
                  { value: "standard", label: t("buzzer.roundModeStandard") },
                ]}
                onChange={v => { setBzRoundMode(v); socket.emit("updateBzRounds", { gameId, mode: v }); }}
              />
            </div>
          )}
          {selectedMode === "buzzer" && bzRoundMode === "choisies" && bzRoundCat.length > 0 && (
            <div class="gl-setting">
              <p>{t("buzzer.roundsOption")}</p>
              <div class="bz-round-picker">
                <span class="bz-joker-hint">{t("buzzer.roundsHint")}</span>
                <div class="bz-round-line">
                {bzRounds.map((cur, i) => (
                  <div key={i} class="bz-round-row">
                    <span class="bz-round-num">{i + 1}</span>
                    <select class="imp-variant-select" value={cur} disabled={!isHost}
                      onChange={e => {
                        const next = [...bzRounds];
                        next[i] = e.target.value;
                        socket.emit("updateBzRounds", { gameId, rounds: next });
                      }}>
                      {bzRoundCat.map(id => (
                        <option key={id} value={id}>
                          {t(`buzzer.kind${id.charAt(0).toUpperCase()}${id.slice(1)}`)}
                        </option>
                      ))}
                    </select>
                  </div>
                ))}
                </div>
              </div>
            </div>
          )}
          {selectedMode === "buzzer" && (
            <div class="gl-setting">
              <p>{t("buzzer.jokerModeOption")}</p>
              <Dropdown
                value={bzJokerMode}
                disabled={!isHost}
                ariaLabel={t("buzzer.jokerModeOption")}
                options={[
                  { value: "impose", label: t("buzzer.jokerModeImposed") },
                  { value: "choix", label: t("buzzer.jokerModeFree") },
                  { value: "aucun", label: t("buzzer.jokerModeNone") },
                ]}
                onChange={v => { setBzJokerMode(v); socket.emit("updateBzJokers", { gameId, mode: v }); }}
              />
            </div>
          )}
          {selectedMode === "buzzer" && bzJokerMode === "impose" && bzCatalogue.length > 0 && (
            <div class="gl-setting">
              <p>{t("buzzer.jokersOption")}</p>
              <div class="bz-joker-picker">
                <span class={`bz-joker-hint${bzJokers.length >= bzMax ? " full" : ""}`}>
                  {t("buzzer.jokersCount", { n: bzJokers.length, max: bzMax })}
                </span>
                <div class="bz-joker-chips">
                  {bzCatalogue.map(id => {
                    const on = bzJokers.includes(id);
                    // Un joker exclu par son jumeau déjà retenu (bouclier / miroir)
                    const clashWith = (bzExclusive.find(pair => pair.includes(id)) || [])
                      .find(o => o !== id && bzJokers.includes(o));
                    const full = !on && bzJokers.length >= bzMax;
                    const label = t(`buzzer.j${id.charAt(0).toUpperCase()}${id.slice(1)}`);
                    return (
                      <button key={id} type="button"
                        disabled={!isHost || !!clashWith || full}
                        title={clashWith
                          ? t("buzzer.jokerExcluded", { other: t(`buzzer.j${clashWith.charAt(0).toUpperCase()}${clashWith.slice(1)}`) })
                          : full ? t("buzzer.jokersFull") : label}
                        class={`bz-joker-chip${on ? " active" : ""}${clashWith ? " excluded" : ""}`}
                        onClick={() => {
                          if (!isHost) return;
                          const next = on ? bzJokers.filter(x => x !== id) : [...bzJokers, id];
                          socket.emit("updateBzJokers", { gameId, jokers: next });
                        }}>
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
          {/* L'Hémicycle partage le réglage : chacun donne un indice par manche. */}
          {(selectedMode === "pictionary" || selectedMode === "hemicycle") && (
            <div class="gl-setting">
              <p>{t("pictionary.roundsOption")}</p>
              <div class="gl-segment">
                {[1, 2, 3, 4, 5, 6].map(n => (
                  <button key={n} type="button" disabled={!isHost}
                    class={`gl-seg-btn${piRounds === n ? " active" : ""}`}
                    onClick={() => { if (!isHost) return; setPiRounds(n); socket.emit("updatePiRounds", { gameId, rounds: n }); }}>
                    {n}
                  </button>
                ))}
              </div>
            </div>
          )}
          {selectedMode === "undercover" && (() => {
            const setUcOpt = (patch) => { if (!isHost) return; setUc(u => ({ ...u, ...patch })); socket.emit("updateUcSettings", { gameId, ...patch }); };
            return (
              <>
                <div class="gl-setting">
                  <p>{t("undercover.endModeOption")}</p>
                  <Dropdown value={uc.endMode} disabled={!isHost} ariaLabel={t("undercover.endModeOption")}
                    options={[
                      { value: "eliminations", label: t("undercover.endEliminations") },
                      { value: "accusation", label: t("undercover.endAccusation") },
                    ]}
                    onChange={v => setUcOpt({ endMode: v })} />
                </div>
                <div class="gl-setting">
                  <p>{t("undercover.countOption")}</p>
                  <div class="gl-segment">
                    {["auto", "1", "2", "3"].map(n => (
                      <button key={n} type="button" disabled={!isHost}
                        class={`gl-seg-btn${uc.count === n ? " active" : ""}`}
                        onClick={() => setUcOpt({ count: n })}>
                        {n === "auto" ? t("undercover.countAuto") : n}
                      </button>
                    ))}
                  </div>
                </div>
                <div class="gl-setting">
                  <p>{t("undercover.whiteOption")}</p>
                  <Dropdown value={uc.white ? "oui" : "non"} disabled={!isHost} ariaLabel={t("undercover.whiteOption")}
                    options={[
                      { value: "non", label: t("undercover.whiteOff") },
                      { value: "oui", label: t("undercover.whiteOn") },
                    ]}
                    onChange={v => setUcOpt({ white: v === "oui" })} />
                </div>
                <div class="gl-setting">
                  <p>{t("undercover.roundsOption")}</p>
                  <div class="gl-segment">
                    {[1, 2, 3, 4, 5].map(n => (
                      <button key={n} type="button" disabled={!isHost}
                        class={`gl-seg-btn${uc.rounds === n ? " active" : ""}`}
                        onClick={() => setUcOpt({ rounds: n })}>
                        {n}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            );
          })()}
          {selectedMode === "references" && (
            <div class="gl-setting">
              <p>{t("references.modeOption")}</p>
              <Dropdown
                value={rfMode}
                disabled={!isHost}
                ariaLabel={t("references.modeOption")}
                options={[
                  { value: "detente", label: t("references.modeChill") },
                  { value: "competitif", label: t("references.modeVersus") },
                ]}
                onChange={v => { setRfMode(v); socket.emit("updateRfMode", { gameId, mode: v }); }}
              />
            </div>
          )}
          {selectedMode === "references" && (
            <div class="gl-setting">
              <p>{t("references.sizeOption")}</p>
              <Dropdown
                value={rfSize}
                disabled={!isHost}
                ariaLabel={t("references.sizeOption")}
                options={[
                  { value: "grande", label: t("references.sizeLarge") },
                  { value: "moyenne", label: t("references.sizeMedium") },
                  { value: "petite", label: t("references.sizeSmall") },
                ]}
                onChange={v => { setRfSize(v); socket.emit("updateRfMode", { gameId, size: v }); }}
              />
            </div>
          )}
          {selectedMode === "references" && (
            <div class="gl-setting">
              <p>{t("references.placementOption")}</p>
              <Dropdown
                value={rfPlacement}
                disabled={!isHost}
                ariaLabel={t("references.placementOption")}
                options={[
                  { value: "facile", label: t("references.placeEasy") },
                  { value: "normal", label: t("references.placeNormal") },
                  { value: "difficile", label: t("references.placeHard") },
                ]}
                onChange={v => { setRfPlacement(v); socket.emit("updateRfMode", { gameId, placement: v }); }}
              />
            </div>
          )}
          {selectedMode === "pixelguess" && (
            <div class="gl-setting">
              <p>{t("pixelGuess.scoringOption")}</p>
              <Dropdown
                value={pgScoring}
                disabled={!isHost}
                ariaLabel={t("pixelGuess.scoringOption")}
                options={[
                  { value: "points", label: t("pixelGuess.scoringPoints") },
                  { value: "wins", label: t("pixelGuess.scoringWins") },
                ]}
                onChange={v => { setPgScoring(v); socket.emit("updatePgScoring", { gameId, scoring: v }); }}
              />
            </div>
          )}
          {selectedMode === "bombe" && (
            <>
              {!boAvailable && (
                <div class="gl-setting wide">
                  <p class="gl-warning">{t("bombe.noDictionary")}</p>
                </div>
              )}
              <div class="gl-setting">
                <p>{t("bombe.livesOption")}</p>
                <div class="gl-segment">
                  {BO_LIVES_CHOICES.map(n => (
                    <button key={n} type="button" disabled={!isHost}
                      class={`gl-seg-btn${boLives === n ? " active" : ""}`}
                      onClick={() => { if (!isHost) return; setBoLives(n); socket.emit("updateBoOptions", { gameId, lives: n }); }}>
                      <span class="gl-hearts">{Array.from({ length: n }, (_, k) => <Heart key={k} />)}</span>
                    </button>
                  ))}
                </div>
              </div>
              <div class="gl-setting">
                <p>{t("bombe.difficultyOption")}</p>
                <Dropdown
                  value={boDifficulty}
                  disabled={!isHost}
                  ariaLabel={t("bombe.difficultyOption")}
                  options={[
                    { value: "facile", label: t("bombe.diffEasy") },
                    { value: "normal", label: t("bombe.diffNormal") },
                    { value: "difficile", label: t("bombe.diffHard") },
                  ]}
                  onChange={v => { setBoDifficulty(v); socket.emit("updateBoOptions", { gameId, difficulty: v }); }}
                />
              </div>
            </>
          )}
          {selectedMode === "battleroyale" && (
            <div class="gl-setting">
              <p>{t("battleRoyale.livesOption")}</p>
              <div class="gl-segment">
                {BR_LIVES_CHOICES.map(n => (
                  <button key={n} type="button" disabled={!isHost}
                    class={`gl-seg-btn${brLives === n ? " active" : ""}`}
                    onClick={() => { if (!isHost) return; setBrLives(n); socket.emit("updateBrLives", { gameId, lives: n }); }}>
                    <span class="gl-hearts">{Array.from({ length: n }, (_, k) => <Heart key={k} />)}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          {!["bombe", "pictionary", "hemicycle", "undercover", "buzzer", "smashpass"].includes(selectedMode) && (
          <div class="gl-setting">
            <p>{selectedMode === "pixelguess" ? t("pixelGuess.roundsOption")
              : selectedMode === "references" ? t("references.countOption")
              : t("lobby.numQuestions")}</p>
            <div class="gl-segment">
              {NUM_QUESTIONS_CHOICES.map(n => (
                <button key={n} type="button" disabled={!isHost}
                  class={`gl-seg-btn${numQuestions === n ? " active" : ""}`}
                  onClick={() => handleNumQuestionsChange(n)}>
                  {n}
                </button>
              ))}
            </div>
          </div>
          )}
          {/* Le rythme de la Bombe et de Pixel Guess ne vient pas d'un temps par
              question : mèche cachée pour l'une, paliers de netteté pour l'autre. */}
          {selectedMode !== "bombe" && selectedMode !== "pixelguess" && selectedMode !== "references" && selectedMode !== "pictionary" && selectedMode !== "hemicycle" && selectedMode !== "undercover" && (
          <div class="gl-setting">
            <p>{t("lobby.questionTime")}</p>
            <div class="gl-segment">
              {QUESTION_TIME_CHOICES.map(n => (
                <button key={n} type="button" disabled={!isHost}
                  class={`gl-seg-btn${questionTime === n ? " active" : ""}`}
                  onClick={() => handleQuestionTimeChange(n)}>
                  {t("lobby.seconds", { count: n })}
                </button>
              ))}
            </div>
          </div>
          )}
        </div>
      </div>

      {/* Lancer, c'est l'action secondaire de la maquette : orange, pas jaune —
          le jaune reste à la création d'une partie. */}
      <div class="gl-actions">
        {isHost ? (
          <button class="btn btn-secondary" onClick={handleStart} disabled={players.length === 0}>{t("lobby.start")}</button>
        ) : (
          <button class="btn btn-primary" disabled>{t("common.waitingForHost")}</button>
        )}
        {isHost && (
          <button class="btn btn-danger" onClick={handleStop}>{t("lobby.closeLobby")}</button>
        )}
        <button class="btn" onClick={handleLeave}>{t("lobby.leaveLobby")}</button>
      </div>
    </div>
  );
}

export default GameLobby;
